import PropTypes from 'prop-types';
import React from 'react';

import { isSimple } from 'src/util/timeOfUse';

import Detailed from './Detailed';
import DetailedInline from './DetailedInline';
import Simple from './Simple';
import SimpleInline from './SimpleInline';

/**
 * Renders a formatted time of usable with formatted value (price, rate) and time conditions.
 * It determines whether to render a simple time of usable (applies to all times)
 * or a complex time of usable with specific time-of-use restrictions.
 * @param {object} props - Component props
 * @param {object} props.timeOfUsable - The time-of-usable object containing pricing
 *   and scheduling data (uuid, price, monthsOfYear, daysOfWeek, timesOfDay, and
 *   optional timezone/holiday settings)
 * @param {boolean} props.showConditions - Whether to display additional conditions
 * such as timezone and holiday settings
 * @param {boolean} props.oneLiner - Whether to render the time of usable as a single line
 * @param {string} [props.valueField='price'] - The property name within the time of usable object
 * @returns {React.ReactElement} Formatted time of usable content with value and time description,
 * or Loading component if time of usable is null
 */
function TimeOfUsable({
  timeOfUsable, showConditions, valueField, oneLiner = false,
}) {
  if (isSimple(timeOfUsable)) {
    if (oneLiner) {
      return <SimpleInline timeOfUsable={timeOfUsable} valueField={valueField} />;
    }
    return <Simple timeOfUsable={timeOfUsable} valueField={valueField} />;
  }

  if (oneLiner) {
    return (
      <DetailedInline
        timeOfUsable={timeOfUsable}
        showConditions={showConditions}
        valueField={valueField}
      />
    );
  }

  return (
    <Detailed timeOfUsable={timeOfUsable} showConditions={showConditions} valueField={valueField} />
  );
}

TimeOfUsable.propTypes = {
  timeOfUsable: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  showConditions: PropTypes.bool.isRequired,
  valueField: PropTypes.string,
  oneLiner: PropTypes.bool,
};

TimeOfUsable.defaultProps = {
  oneLiner: false,
  valueField: 'price',
};

export default TimeOfUsable;
